/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { CmlContent } from '__generated__/graphql-types-do-not-use';

import { Tag, Typography2 } from '@coursera/cds-core';

import AvailabilityDetails from 'bundles/unified-description-page-common/components/modules/syllabus/AvailabilityDetails';
import LearningObjective from 'bundles/unified-description-page-common/components/shared/LearningObjective';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import TogglableDescription from 'bundles/unified-description-page-common/components/shared/TogglableDescription';
import NewAiBadge from 'bundles/unified-description-page-common/components/shared/badges/NewAiBadge';
import type { FixedSessionFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedEnterpriseEntitiesFragments';
import { getGoogleRefreshAiBadgeContent } from 'bundles/unified-description-page-common/utils/getGoogleRefreshAiBadgeContent';
import { shouldShowGoogleRefreshXdpAiSyllabus } from 'bundles/unified-description-page-common/utils/shouldShowGoogleRefresh';

import _t from 'i18n!nls/xdp';

export type CourseItemContentElement = React.ReactElement<Props, typeof CourseItemContent>;

type Props = {
  learningObjectives?: CmlContent[] | null;
  description?: string | null;
  cmlDescription?: CmlContent | null;
  skills?: string[] | null;
  fixedSession?: FixedSessionFragment | null;
  s12nSlug?: string;
  courseSlug?: string;
};

const styles = {
  skills: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-200);
  `,
  learningObjective: css`
    & + & {
      padding-top: var(--cds-spacing-100);
    }
  `,
  aiContent: css`
    margin-top: calc(var(--cds-spacing-50) * -1);

    ul {
      padding-left: 1.5em;

      li {
        list-style: disc;
      }
    }
  `,
};

const CourseItemContent = ({
  learningObjectives,
  description,
  skills,
  cmlDescription,
  fixedSession,
  s12nSlug,
  courseSlug,
}: Props) => {
  const hasLearningObjectives = learningObjectives && learningObjectives?.length > 0;
  return (
    <div>
      {fixedSession && <AvailabilityDetails fixedSession={fixedSession} />}
      {(hasLearningObjectives || description) && (
        <Subsection
          title={
            <Typography2 variant="subtitleMedium" component="h4">
              {_t("What you'll learn")}
            </Typography2>
          }
        >
          {hasLearningObjectives ? (
            <ul>
              {learningObjectives.map((objective, index) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={index} css={styles.learningObjective}>
                    <LearningObjective objective={objective} />
                  </li>
                );
              })}
            </ul>
          ) : (
            <TogglableDescription description={description} cmlDescription={cmlDescription} />
          )}
        </Subsection>
      )}
      {shouldShowGoogleRefreshXdpAiSyllabus(s12nSlug, courseSlug) && (
        <Subsection title={<NewAiBadge slug={s12nSlug} disabled />}>
          <Typography2 variant="bodyPrimary" component="div" css={styles.aiContent}>
            {getGoogleRefreshAiBadgeContent(s12nSlug)}
          </Typography2>
        </Subsection>
      )}
      {skills && skills.length > 0 && (
        <Subsection
          title={
            <Typography2 variant="subtitleMedium" component="h4">
              {_t("Skills you'll gain")}
            </Typography2>
          }
        >
          <div css={styles.skills}>
            {skills.map((skill, index) => {
              return (
                <Tag
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                >
                  {skill}
                </Tag>
              );
            })}
          </div>
        </Subsection>
      )}
    </div>
  );
};

export default CourseItemContent;
